import {DataTestAttrs} from '@benchling/js-test-utils';
import React from 'react';

import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';
import {
  SectionsSummary,
  SectionsSummaryProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeSummary';
import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {
  StandardHeader,
  StandardHeaderProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/standard/StandardHeader';
import {
  StandardSections,
  StandardSectionsProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/standard/StandardSections';

export interface StandardNodeProps {
  /** Whether the node should be disabled */
  isDisabled?: boolean;
  /** Whether the node is selected */
  isSelected?: boolean;
  /** Whether the node can be selected */
  isSelectable?: boolean;
  /** The current status of the node */
  status?: BenchlingNodeV2Status;
  /** A description of the node's status. When provided, this will be shown in a tooltip on the status icon */
  statusDescription?: string;
  /** A {@link StandardHeaderProps}. If not provided, no header will be rendered. */
  headerProps?: StandardHeaderProps;
  /** A {@link StandardSectionsProps}. If not provided, no sections will be rendered. */
  sectionsProps?: StandardSectionsProps;
  /** A {@link SectionsSummaryProps}. If not provided, no sections summary will be rendered. */
  sectionsSummaryProps?: SectionsSummaryProps;
  /** Optional width override for the node. If not provided, uses BenchlingNodeV2's default width. */
  width?: string;
  /** Data test attributes for the node */
  dataTestAttrs?: Omit<DataTestAttrs, 'component'>;
}

/**
 * A standard flowchart node.
 * @package
 */
export function StandardNode(props: StandardNodeProps): JSX.Element {
  const {
    isDisabled,
    isSelected,
    isSelectable = true,
    headerProps,
    sectionsProps,
    sectionsSummaryProps,
    status,
    statusDescription,
    width,
    dataTestAttrs,
  } = props;
  return (
    <BenchlingNodeV2
      isSelected={isSelected}
      isSelectable={isSelectable}
      isDisabled={isDisabled}
      status={status}
      statusDescription={statusDescription}
      w={width}
      dataTestAttrs={dataTestAttrs}
    >
      {headerProps != null && <StandardHeader {...headerProps} />}
      {sectionsSummaryProps != null && <SectionsSummary {...sectionsSummaryProps} />}
      {sectionsProps != null && <StandardSections {...sectionsProps} />}
    </BenchlingNodeV2>
  );
}
